import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { LogIn, Mail, Lock, Eye, EyeOff, Building2, GraduationCap, Users, UserCheck } from 'lucide-react';
import axios from 'axios';
import Footer from '../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TenantLogin = () => {
  const { tenantCode } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tenant, setTenant] = useState(null);
  const [tenantLoading, setTenantLoading] = useState(true);
  const [serverError, setServerError] = useState(false);
  const [errorType, setErrorType] = useState('server');

  useEffect(() => {
    fetchTenantInfo();
  }, [tenantCode]);

  const fetchTenantInfo = async () => {
    try {
      setTenantLoading(true);
      setServerError(false);
      const response = await axios.get(`${API_URL}/api/tenants/by-subdomain/${tenantCode}`);
      setTenant(response.data);
      setError('');
    } catch (err) {
      // Check if it's a server/network error
      if (!err.response) {
        setServerError(true);
        setErrorType('network');
      } else if (err.response.status >= 500) {
        setServerError(true);
        setErrorType('server');
      } else {
        // 404 or other client error - org not found
        setError('Organization not found. Please check the URL.');
        setTenant(null);
      }
    } finally {
      setTenantLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      const { access_token, user } = response.data;
      
      // Check if user has a tenant_id and it matches the current tenant
      // Or if user belongs to this organization
      if (user.tenant_id && tenant.id && user.tenant_id !== tenant.id) {
        setError('You do not have access to this organization.');
        setLoading(false);
        return;
      }

      // Store token and tenant info
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('tenant_code', tenantCode);
      
      // Redirect to tenant-specific dashboard
      window.location.href = `/${tenantCode}/dashboard`;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading organization...</p>
        </div>
      </div>
    );
  }

  // Show server error page
  if (serverError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className={`w-20 h-20 ${errorType === 'network' ? 'bg-orange-100' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto`}>
              <svg className={`w-10 h-10 ${errorType === 'network' ? 'text-orange-500' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {errorType === 'network' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                )}
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-3">
            {errorType === 'network' ? 'No Internet Connection' : 'Server Temporarily Unavailable'}
          </h1>
          <p className="text-gray-600 mb-6">
            {errorType === 'network' 
              ? 'Please check your internet connection and try again.'
              : 'We are experiencing technical difficulties. Please try again in a few moments.'}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800">
              {errorType === 'network' 
                ? 'Check your WiFi or mobile data connection'
                : 'Our team has been notified and is working on it'}
            </p>
          </div>
          <button
            onClick={() => fetchTenantInfo()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Try Again
          </button>
          <p className="text-xs text-gray-500 mt-6">
            If the problem persists, please contact support
          </p>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Organization Not Found</h1>
          <p className="text-gray-600 mb-6">
            The organization code "<strong>{tenantCode}</strong>" does not exist or is inactive.
          </p>
          <a 
            href="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            Go to Main Site
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4 relative">
      <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header with Tenant Info */}
        <div className="text-center mb-5">
          {tenant.logo_url ? (
            <img 
              src={tenant.logo_url} 
              alt={tenant.name} 
              className="h-14 w-auto object-contain mx-auto mb-3"
            />
          ) : (
            <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Building2 className="w-7 h-7 text-blue-600" />
            </div>
          )}
          <h1 className="text-xl font-bold text-gray-800">{tenant.name}</h1>
          <p className="text-gray-500 text-xs mt-1">cloudcampus.site/{tenantCode}</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email / Employee ID
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="email@example.com or Employee ID"
                required
                data-testid="tenant-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 text-gray-400" size={18} />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                placeholder="••••••••"
                required
                data-testid="tenant-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-blue-300"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Portal Links */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-center text-sm text-gray-500 mb-3">Other Portals</p>
          <div className="grid grid-cols-2 gap-2">
            <Link
              to={`/${tenantCode}/student/login`}
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors font-medium text-sm"
              data-testid="student-login-link"
            >
              <GraduationCap size={16} />
              <span>Student</span>
            </Link>
            <Link
              to={`/${tenantCode}/staff-portal`}
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-medium text-sm"
              data-testid="staff-login-link"
            >
              <Users size={16} />
              <span>Staff</span>
            </Link>
            <Link
              to={`/${tenantCode}/company-portal`}
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors font-medium text-sm"
              data-testid="company-login-link"
            >
              <Building2 size={16} />
              <span>Company</span>
            </Link>
            <Link
              to={`/${tenantCode}/alumni-portal`}
              className="flex items-center justify-center gap-2 py-2.5 px-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
              data-testid="alumni-login-link"
            >
              <UserCheck size={16} />
              <span>Alumni</span>
            </Link>
          </div>
        </div>
      </div>
      
      {/* Footer with Policy Links */}
      <div className="absolute bottom-4 left-0 right-0">
        <Footer variant="minimal" tenantSlug={tenantCode} />
      </div>
    </div>
  );
};

export default TenantLogin;
